import React, {
  useEffect,
  useLayoutEffect,
  useState,
  useMemo,
  useCallback,
} from "react";
import { useMeetingAppContext } from "../MeetingAppContextDef";
import { ParticipantView } from "./ParticipantView";
import {
  useParticipant,
  useMeeting,
} from "@videosdk.live/react-sdk";
import useIsMobile from "../hooks/useIsMobile";
import { useSiteContext } from "../context/SiteContext";
import {
  computeDynamicLayout,
  computeOptimalGridLayout,
} from "../utils/dynamicLayout";
import { useParticipantRanking } from "../utils/participantRanking";
import { useBreakoutRooms } from "../context/BreakoutRoomsContext";
import { useSettings } from "../hooks/useSettings";

const ENABLE_DYNAMIC_LAYOUT = true;

// Component to manage pause/resume for participant streams based on visibility and breakout room
function ParticipantStreamController({
  participantId,
  isVisible,
  isPresenting,
  participantIds,
}) {
  const {
    webcamStream,
    micStream,
    stopConsumingWebcamStreams,
    consumeWebcamStreams,
    stopConsumingMicStreams,
    consumeMicStreams,
    setQuality,
  } = useParticipant(participantId, {
    onVideoQualityChanged: (quality) => {
      console.log("quality", quality);
    },
  });

  const { isInSameRoom, currentRoom } = useBreakoutRooms();
  const { localParticipant } = useMeeting();
  
  // Check if this participant should have their streams active
  const shouldConsumeStreams = useMemo(() => {
    if (!currentRoom) return isVisible; // If not in any room, use visibility
    if (participantId === localParticipant?.id) return isVisible; // Always show self
    
    // Check if participant is in the same room
    const inSameRoom = isInSameRoom(participantId);
    return isVisible && inSameRoom;
  }, [isVisible, currentRoom, participantId, isInSameRoom, localParticipant?.id]);

  useEffect(() => {
    if (webcamStream) {
      try {
        if (!shouldConsumeStreams) {
          // Stop consuming webcam streams if not visible or not in same room
          stopConsumingWebcamStreams();
        } else {
          // Consume webcam streams if visible and in same room
          consumeWebcamStreams();
          if (isPresenting) {
            setQuality("low");
          } else {
            if (participantIds.length > 10) {
              setQuality("med");
            } else {
              setQuality("high");
            }
          }
        }
      } catch (error) {
        console.error(`Error controlling webcam stream for ${participantId}:`, error);
      }
    }
  }, [
    shouldConsumeStreams,
    webcamStream,
    stopConsumingWebcamStreams,
    consumeWebcamStreams,
    setQuality,
    isPresenting,
    participantIds.length,
    participantId,
  ]);

  // Control audio stream consumption for breakout room isolation
  useEffect(() => {
    if (micStream) {
      try {
        if (!shouldConsumeStreams) {
          // Stop consuming mic streams if not in same room
          stopConsumingMicStreams();
        } else {
          // Consume mic streams if in same room
          consumeMicStreams();
        }
      } catch (error) {
        console.error(`Error controlling mic stream for ${participantId}:`, error);
      }
    }
  }, [
    shouldConsumeStreams,
    micStream,
    stopConsumingMicStreams,
    consumeMicStreams,
    participantId,
  ]);

  return null;
}

const MemoizedParticipant = React.memo(
  ParticipantView,
  (prevProps, nextProps) => {
    return (
      prevProps.participantId === nextProps.participantId &&
      prevProps.gridSize === nextProps.gridSize &&
      prevProps.totalOnPage === nextProps.totalOnPage &&
      prevProps.isSingleParticipant === nextProps.isSingleParticipant
    );
  }
);

function ParticipantGrid({
  participantIds,
  isPresenting,
  isMeetingContainer,
  isFlexCol,
  layout,
}) {
  const { sideBarMode, activeSpeakerId, leftSideBarMode } =
    useMeetingAppContext();
  const { participants, pinnedParticipants, localParticipant } = useMeeting();
  const isMobile = useIsMobile();
  console.log("isMobile", isMobile);
  const [participantsPerPage, setParticipantsPerPage] = useState(6);
  const [currentPage, setCurrentPage] = useState(0);
  const [gridCurrentPage, setGridCurrentPage] = useState(0);
  const { meetingSettings, localPinnedParticipant } = useSiteContext();
  const { adminSettings } = useSettings();

  console.log("adminSettings", adminSettings);


  const participantThresholdPanel = useMemo(() => {
    const setting = adminSettings?.find(s => s?.key === "ParticipantThresholdPanel");
    return setting?.value ?? 25; 
  }, [adminSettings]);

  const participantThresholdPanelMobile = useMemo(() => {
    const setting = adminSettings?.find(s => s?.key === "ParticipantThresholdPanelMobile");
    return setting?.value ?? 8;
  }, [adminSettings]);

  const participantFrequencyLevel = useMemo(() => {
    const setting = adminSettings?.find(s => s?.key === "ParticipantFrequencyLevel");
    return setting?.value ?? 2; 
  }, [adminSettings]);


  const gridParticipantsPerPage = useMemo(() => {
    return isMobile ? participantThresholdPanelMobile : participantThresholdPanel;
  }, [isMobile, participantThresholdPanelMobile, participantThresholdPanel]);

  const pinnedIds = useMemo(() => {
    const ids = [];
    try {
      if (pinnedParticipants?.keys) {
        ids.push(...Array.from(pinnedParticipants.keys()));
      }
    } catch (e) {
      // ignore
    }
    if (Array.isArray(localPinnedParticipant)) {
      ids.push(...localPinnedParticipant);
    }
    return ids;
  }, [pinnedParticipants, localPinnedParticipant]);


  const rankingConfig = useMemo(() => {
    const refreshIntervalMs = participantFrequencyLevel * 1000; 
    const topCount = isMobile ? participantThresholdPanelMobile : participantThresholdPanel;
    return {
      tickIntervalMs: refreshIntervalMs,
      reorderIntervalMs: refreshIntervalMs, 
      transitionQueueMs: Math.max(150, refreshIntervalMs * 0.3),
      topParticipantsCount: topCount,
      topParticipantsUpdateMs: refreshIntervalMs, 
    };
  }, [isMobile, participantFrequencyLevel, participantThresholdPanel, participantThresholdPanelMobile]);

  const { orderedIds: orderedParticipantIds } = useParticipantRanking({
    participantIds,
    participants,
    activeSpeakerId,
    pinnedIds,
    selfId: localParticipant?.id,
    enabled: meetingSettings?.allowParticipantRanking ?? false,
    config: rankingConfig,
  });

  // Get minimized state from context
  const { isParticipantRowMinimized } = useMeetingAppContext();

  // Drag state for pagination panel
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [panelPosition, setPanelPosition] = useState({ x: 0, y: 0 }); // Will be set to bottom-right in useEffect

  const totalParticipants = orderedParticipantIds.length;

  // Desktop grid viewport measurement (for Meet-like dynamic tiling)
  const [gridViewportEl, setGridViewportEl] = useState(null);
  const gridViewportRef = useCallback((node) => {
    if (node) setGridViewportEl(node);
  }, []);
  const [gridViewport, setGridViewport] = useState({ width: 0, height: 0 });

  useLayoutEffect(() => {
    // Only needed for desktop, non-presenting grid.
    if (isMobile || isPresenting) return;

    const el = gridViewportEl;
    if (!el) return;

    let raf = 0;
    const update = () => {
      const rect = el.getBoundingClientRect();
      setGridViewport((prev) => {
        if (prev.width === rect.width && prev.height === rect.height)
          return prev;
        return { width: rect.width, height: rect.height };
      });
    };

    update();
    raf = window.requestAnimationFrame(() => update());
    const raf2 = window.requestAnimationFrame(() => update());

    if (!window.ResizeObserver) {
      window.addEventListener("resize", update);
      return () => {
        window.cancelAnimationFrame(raf);
        window.cancelAnimationFrame(raf2);
        window.removeEventListener("resize", update);
      };
    }

    const ro = new ResizeObserver(() => update());
    ro.observe(el);
    window.addEventListener("resize", update);
    return () => {
      window.cancelAnimationFrame(raf);
      window.cancelAnimationFrame(raf2);
      window.removeEventListener("resize", update);
      ro.disconnect();
    };
  }, [gridViewportEl, isMobile, isPresenting]);

  useEffect(() => {
    const pageSize = isMobile && !isPresenting ? 8 : gridParticipantsPerPage;
    const maxPages = Math.ceil(totalParticipants / pageSize);

    if (gridCurrentPage >= maxPages && maxPages > 0) {
      setGridCurrentPage(maxPages - 1);
    }
  }, [
    totalParticipants,
    gridParticipantsPerPage,
    isMobile,
    isPresenting,
    gridCurrentPage,
  ]);

  useEffect(() => {
    if (isPresenting) {
      const maxPages = Math.ceil(totalParticipants / participantsPerPage);

      if (currentPage >= maxPages && maxPages > 0) {
        setCurrentPage(maxPages - 1);
      }
    }
  }, [totalParticipants, participantsPerPage, currentPage, isPresenting]);

  useEffect(() => {
    // Handle sidebar mode changes without resetting pages
    if (leftSideBarMode && sideBarMode) {
      setParticipantsPerPage(4);
    } else if (sideBarMode) {
      setParticipantsPerPage(4);
    } else {
      if (isFlexCol) {
        setParticipantsPerPage(5);
      } else {
        setParticipantsPerPage(6);
      }
    }
  }, [sideBarMode, leftSideBarMode, isFlexCol]);

  // Set initial position to bottom-right and handle window resize
  useEffect(() => {
    const updatePosition = () => {
      setPanelPosition({
        x: window.innerWidth - 200, // 200px from right edge
        y: window.innerHeight - 80, // 80px from bottom edge
      });
    };

    // Set initial position
    updatePosition();

    // Handle window resize
    window.addEventListener("resize", updatePosition);

    return () => {
      window.removeEventListener("resize", updatePosition);
    };
  }, []);

  // Drag handlers for pagination panel
  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsDragging(true);
    const rect = e.currentTarget.getBoundingClientRect();
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
  };

  const handleMouseMove = useCallback(
    (e) => {
      if (!isDragging) return;

      const newX = e.clientX - dragOffset.x;
      const newY = e.clientY - dragOffset.y;

      // Constrain to viewport bounds
      const maxX = window.innerWidth - 200; // Approximate panel width
      const maxY = window.innerHeight - 60; // Approximate panel height

      setPanelPosition({
        x: Math.max(0, Math.min(newX, maxX)),
        y: Math.max(0, Math.min(newY, maxY)),
      });
    },
    [isDragging, dragOffset]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Add global mouse event listeners when dragging
  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.body.style.userSelect = "none"; // Prevent text selection during drag

      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
        document.body.style.userSelect = "";
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const allParticipantIds = orderedParticipantIds;

  // Helper function to check if a participant is currently visible
  const isParticipantVisible = (index) => {
    if (isPresenting) {
      return index >= currentPage && index < currentPage + participantsPerPage;
    } else {
      const startIndex = gridCurrentPage * gridParticipantsPerPage;
      return (
        index >= startIndex && index < startIndex + gridParticipantsPerPage
      );
    }
  };

  // Mobile-specific layout for grid view
  if (isMobile && !isPresenting) {
    const MOBILE_PAGE_SIZE = 8;

    const firstPageParticipants = allParticipantIds.slice(0, MOBILE_PAGE_SIZE);
    const remainingParticipants = allParticipantIds.slice(MOBILE_PAGE_SIZE);

    const remainingPages =
      remainingParticipants.length > 0
        ? Math.ceil(remainingParticipants.length / MOBILE_PAGE_SIZE)
        : 0;

    const totalPages = 1 + remainingPages;

    let visibleParticipants;
    if (gridCurrentPage === 0) {
      visibleParticipants = firstPageParticipants;
    } else {
      const remainingPageIndex = gridCurrentPage - 1;
      const startIndex = remainingPageIndex * MOBILE_PAGE_SIZE;
      const endIndex = startIndex + MOBILE_PAGE_SIZE;
      visibleParticipants = remainingParticipants.slice(startIndex, endIndex);
    }

    const visibleParticipantsSet = new Set(visibleParticipants);

    const mapIdsToDebugInfo = (ids) => {
      if (!ids || !Array.isArray(ids)) return [];
      return ids.map((id) => {
        const participant = participants?.get ? participants.get(id) : null;
        const name =
          participant?.displayName ||
          participant?.name ||
          (participant?.metaData && participant.metaData.name) ||
          id;
        return { id, name };
      });
    };

    const activeSpeakerParticipant = participants?.get
      ? participants.get(activeSpeakerId)
      : null;
    const activeSpeakerName =
      activeSpeakerParticipant?.displayName ||
      activeSpeakerParticipant?.name ||
      (activeSpeakerParticipant?.metaData &&
        activeSpeakerParticipant.metaData.name) ||
      activeSpeakerId;

    console.log("[MobileGrid]", {
      activeSpeakerId,
      activeSpeakerName,
      firstPageParticipants: mapIdsToDebugInfo(firstPageParticipants),
      remainingParticipants: mapIdsToDebugInfo(remainingParticipants),
      gridCurrentPage,
      visibleParticipants: mapIdsToDebugInfo(visibleParticipants),
    });

    const getTileAspectRatio = (pid) => {
      return "16/9";
    };
    const isMobileParticipant = (pid) => {
      const p = participants?.get ? participants.get(pid) : null;
      const meta = p?.metaData || {};
      const deviceType =
        meta.deviceType || (meta.isMobile ? "mobile" : "desktop");
      return deviceType === "mobile";
    };

    return (
      <div className="flex flex-col flex-grow min-w-0 w-full h-full overflow-x-hidden">
        <div
          className={`relative flex flex-col flex-grow px-3 py-3 box-border items-stretch w-full max-w-full min-w-0 h-full overflow-hidden ${
            visibleParticipants.length === 1
              ? "justify-center"
              : visibleParticipants.length <= 5
              ? "justify-center"
              : "justify-start"
          }`}
        >
          {/* Render activity collectors for all participants */}
          {/* Render stream controllers for all participants */}
          {allParticipantIds.map((participantId) => (
            <ParticipantStreamController
              key={`stream_controller_${participantId}`}
              participantId={participantId}
              isVisible={visibleParticipantsSet.has(participantId)}
              isPresenting={isPresenting}
              participantIds={allParticipantIds}
            />
          ))}

          {/* Current page participants -  layout for 2 participants on mobile */}
          {visibleParticipants.length > 0 && (
            <div
              className={`w-full min-w-0 overflow-hidden flex ${
                visibleParticipants.length === 1
                  ? "flex-1 h-full"
                  : visibleParticipants.length <= 5
                  ? "items-center justify-center flex-shrink-0"
                  : "items-stretch justify-start flex-1"
              }`}
            >
              {visibleParticipants.length === 1 ? (
                <div className="flex items-center justify-center overflow-hidden rounded-lg w-full h-full flex-1">
                  <MemoizedParticipant
                    participantId={visibleParticipants[0]}
                    isSingleParticipant={true}
                    gridSize={1}
                    totalOnPage={1}
                    isMobileParticipant={isMobileParticipant(
                      visibleParticipants[0]
                    )}
                  />
                </div>
              ) : visibleParticipants.length === 2 ? (
                <div className="flex flex-col gap-px w-full max-w-full min-w-0 items-stretch justify-center">
                  {visibleParticipants.map((participantId) => (
                    <div
                      key={`mobile_${participantId}`}
                      className="flex items-center justify-center overflow-hidden rounded-lg min-w-0 w-full"
                      style={{
                        aspectRatio: getTileAspectRatio(participantId),
                        minHeight: 0,
                      }}
                    >
                      <MemoizedParticipant
                        participantId={participantId}
                        gridSize={1}
                        totalOnPage={2}
                        isMobileParticipant={isMobileParticipant(participantId)}
                      />
                    </div>
                  ))}
                </div>
              ) : (
                (() => {
                  const left = [];
                  const right = [];
                  let leftH = 0;
                  let rightH = 0;

                  const weightFor = (pid) => {
                    const arStr = getTileAspectRatio(pid);
                    const parts = String(arStr).split("/");
                    const ar =
                      parts.length === 2
                        ? Number(parts[0]) / Number(parts[1])
                        : Number(arStr);

                    return ar > 0 ? 1 / ar : 1;
                  };

                  visibleParticipants.forEach((pid) => {
                    const w = weightFor(pid) + 0.05;
                    if (leftH <= rightH) {
                      left.push(pid);
                      leftH += w;
                    } else {
                      right.push(pid);
                      rightH += w;
                    }
                  });

                  const renderTile = (participantId) => (
                    <div
                      key={`mobile_${participantId}`}
                      className="flex items-center justify-center overflow-hidden rounded-lg w-full min-w-0"
                      style={{
                        aspectRatio: getTileAspectRatio(participantId),
                        minWidth: 0,
                        minHeight: 0,
                      }}
                    >
                      <MemoizedParticipant
                        participantId={participantId}
                        gridSize={2}
                        totalOnPage={visibleParticipants.length}
                        isMobileParticipant={isMobileParticipant(participantId)}
                      />
                    </div>
                  );

                  const masonryContent = (
                    <div className="flex w-full max-w-full gap-px items-start">
                      <div className="flex-1 min-w-0 flex flex-col gap-px">
                        {left.map(renderTile)}
                      </div>
                      <div className="flex-1 min-w-0 flex flex-col gap-px">
                        {right.map(renderTile)}
                      </div>
                    </div>
                  );

                  // For fewer participants, wrap in centered container
                  if (visibleParticipants.length <= 5) {
                    return (
                      <div className="flex items-center justify-center w-full flex-shrink-0">
                        {masonryContent}
                      </div>
                    );
                  }

                  return masonryContent;
                })()
              )}
            </div>
          )}

          {allParticipantIds.map((participantId) => {
            if (visibleParticipants.includes(participantId)) {
              return null;
            }
            return (
              <div key={`hidden_${participantId}`} style={{ display: "none" }}>
                <MemoizedParticipant participantId={participantId} />
              </div>
            );
          })}

          {totalPages > 1 && (
            <div className="mt-3 flex w-full items-center justify-center">
              <div className="inline-flex items-center gap-3 bg-[#1C1F2E]/95 backdrop-blur-md rounded-lg px-4 py-2 shadow-lg border border-gray-700/60">
                {/* Previous Button */}
                <button
                  onClick={() => {
                    if (gridCurrentPage > 0) {
                      setGridCurrentPage(gridCurrentPage - 1);
                    }
                  }}
                  disabled={gridCurrentPage === 0}
                  className={`p-1.5 rounded transition-all duration-150 ${
                    gridCurrentPage === 0
                      ? "opacity-30 cursor-not-allowed"
                      : "hover:bg-white/10 active:scale-95"
                  }`}
                  title="Previous page"
                >
                  <img src="/icons/arrowLeft.svg" alt="Previous page" />
                </button>

                {/* Page Counter */}
                <div className="flex items-center gap-1 text-white text-sm font-medium px-2">
                  <span>{gridCurrentPage + 1}</span>
                  <span className="text-gray-400">/</span>
                  <span>{totalPages}</span>
                </div>

                {/* Next Button */}
                <button
                  onClick={() => {
                    if (gridCurrentPage < totalPages - 1) {
                      setGridCurrentPage(gridCurrentPage + 1);
                    }
                  }}
                  disabled={gridCurrentPage >= totalPages - 1}
                  className={`p-1.5 rounded transition-all duration-150 ${
                    gridCurrentPage >= totalPages - 1
                      ? "opacity-30 cursor-not-allowed"
                      : "hover:bg-white/10 active:scale-95"
                  }`}
                  title="Next page"
                >
                  <img src="/icons/arrowRight.svg" alt="Next page" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Desktop and Presenting Layout (unchanged)
  return (
    <>
      <div
        className={`relative flex flex-col md:flex-row flex-grow items-center justify-center min-h-0 ${
          isPresenting
            ? "m-0 p-2 md:p-3"
            : !isMobile && !isPresenting
            ? "m-0 p-0" // No margins/padding for desktop non-presenting (Meet-like layout fills screen)
            : totalParticipants < 2 && !sideBarMode && !isPresenting
            ? "m-3 md:px-2 md:py-2"
            : totalParticipants < 3 && !sideBarMode && !isPresenting
            ? "m-3 md:px-2 md:py-8"
            : totalParticipants < 4 && !sideBarMode && !isPresenting
            ? "m-3 md:px-2 md:py-4"
            : totalParticipants >= 5 &&
              totalParticipants <= 6 &&
              !sideBarMode &&
              !isPresenting
            ? "m-1 md:px-0 md:py-1"
            : totalParticipants > 4 && !sideBarMode && !isPresenting
            ? "m-3 md:px-0"
            : "m-3 md:px-0"
        }`}
        style={{
          backgroundImage:
            isPresenting || meetingSettings?.customBackgroundEnabled
              ? "none"
              : `url(/meetingBg.png)`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      >
        {/* Render stream controllers for all participants */}
        {allParticipantIds.map((participantId, idx) => (
          <ParticipantStreamController
            key={`stream_controller_${participantId}`}
            participantId={participantId}
            isVisible={isParticipantVisible(idx)}
            isPresenting={isPresenting}
            participantIds={allParticipantIds}
          />
        ))}

        <div
          className={`flex ${
            isFlexCol && isPresenting
              ? `flex-col gap-px px-2 ${isMobile ? "overflow-hidden" : "overflow-y-auto overflow-x-hidden"}`
              : ""
          }  ${
            isPresenting
              ? `flex-row justify-start md:justify-center items-center gap-px overflow-x-auto participant-grid-scrollbar transition-all duration-300 ${
                  isParticipantRowMinimized
                    ? "h-16 opacity-100 py-1"
                    : "h-auto opacity-100 py-2"
                }`
              : `flex-col gap-0 items-stretch min-h-0 ${
                  !isPresenting &&
                  totalParticipants >= 5 &&
                  totalParticipants <= 6
                    ? "justify-center"
                    : "justify-start"
                }`
          } w-full ${
            isPresenting
              ? isParticipantRowMinimized
                ? "h-16"
                : isFlexCol
                ? "h-full"
                : "h-auto"
              : "h-full min-h-0 overflow-hidden"
          } relative`}
          style={
            !isPresenting && !isMobile
              ? {
                  // No maxWidth or margin for desktop Meet-like layout - fill full space
                  width: "100%",
                  height: "100%",
                  maxHeight: "100%",
                }
              : !isPresenting
              ? {
                  maxWidth:
                    totalParticipants >= 2 && totalParticipants <= 3
                      ? "1800px"
                      : totalParticipants >= 5 && totalParticipants <= 6
                      ? "2000px"
                      : "1400px",
                  margin: "0 auto",
                  maxHeight: "100%",
                }
              : {}
          }
        >
          {/* Left Arrow Icon */}
          {isPresenting && totalParticipants > participantsPerPage && (
            <>
              {isFlexCol && (
                <div className="flex justify-center items-center py-2">
                  <button
                    onClick={() => {
                      if (currentPage === 0) {
                        return;
                      }
                      setCurrentPage(currentPage - 1);
                    }}
                    disabled={currentPage === 0}
                    className={`flex items-center justify-center w-8 h-8 rounded-md transition-all duration-200 ${
                      currentPage === 0
                        ? "bg-gray-700 cursor-not-allowed opacity-40"
                        : "bg-gray-800 hover:bg-gray-700 cursor-pointer"
                    }`}
                    style={{
                      zIndex: 10,
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={2.5}
                      stroke="currentColor"
                      className="w-4 h-4 text-white"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M8.25 15.75l7.5-7.5 7.5 7.5"
                      />
                    </svg>
                  </button>
                </div>
              )}
              {!isFlexCol && (
                <svg
                  onClick={() => {
                    if (currentPage === 0) {
                      return;
                    }
                    setCurrentPage(currentPage - 1);
                  }}
                  disabled={currentPage === 0}
                  style={{
                    zIndex: 1000,
                    color: "#ffffff",
                    backgroundColor: currentPage === 0 ? "gray" : "#1a1c23",
                    strokeWidth: 4,
                    cursor: "pointer",
                  }}
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="w-6 h-full"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15.75 19.5 8.25 12l7.5-7.5"
                  />
                </svg>
              )}
            </>
          )}

          {isPresenting && (
            <>
              {/* Render all participants for audio, but hide non-current page ones */}
              {allParticipantIds.map((participantId, idx) => {
                const isOnCurrentPage =
                  idx >= currentPage && idx < currentPage + participantsPerPage;
                if (!isOnCurrentPage) {
                  return (
                    <div
                      key={`participant_hidden_${participantId}`}
                      style={{ display: "none" }}
                    >
                      <MemoizedParticipant
                        participantId={participantId}
                        gridSize={Math.min(participantsPerPage, 7)}
                        totalOnPage={participantsPerPage}
                      />
                    </div>
                  );
                }
                return null;
              })}

              {/* Display visible participants with layout */}
              {allParticipantIds
                .slice(currentPage, currentPage + participantsPerPage)
                .map((participantId) => {
                  // Calculate sizes based on minimized state
                  const getParticipantSize = () => {
                    if (isFlexCol) {
                      return "w-full";
                    }
                    if (isParticipantRowMinimized) {
                      // Smaller sizes when minimized
                      return totalParticipants === 1
                        ? "h-12 w-[80px] sm:h-14 sm:w-[100px] md:h-14 md:w-[120px]"
                        : totalParticipants === 2
                        ? "h-12 w-[70px] sm:h-14 sm:w-[90px] md:h-14 md:w-[110px]"
                        : totalParticipants === 3
                        ? "h-11 w-[60px] sm:h-12 sm:w-[80px] md:h-13 md:w-[100px]"
                        : totalParticipants === 4
                        ? "h-10 w-[55px] sm:h-11 sm:w-[70px] md:h-12 md:w-[90px]"
                        : "h-10 w-[50px] sm:h-11 sm:w-[65px] md:h-12 md:w-[85px]";
                    } else {
                      // Normal sizes when expanded
                      return totalParticipants === 1
                        ? "h-[120px] w-[200px] sm:h-[140px] sm:w-[240px] md:h-[180px] md:w-[320px] xl:h-[200px] xl:w-[360px] 2xl:h-[220px] 2xl:w-[400px]"
                        : totalParticipants === 2
                        ? "h-[100px] w-[180px] sm:h-[120px] sm:w-[200px] md:h-[160px] md:w-[280px] xl:h-[180px] xl:w-[320px] 2xl:h-[200px] 2xl:w-[360px]"
                        : totalParticipants === 3
                        ? "h-[90px] w-[160px] sm:h-[110px] sm:w-[180px] md:h-[140px] md:w-[240px] xl:h-[160px] xl:w-[280px] 2xl:h-[180px] 2xl:w-[320px]"
                        : totalParticipants === 4
                        ? "h-[80px] w-[140px] sm:h-[100px] sm:w-[160px] md:h-[120px] md:w-[200px] xl:h-[140px] xl:w-[240px] 2xl:h-[160px] 2xl:w-[280px]"
                        : "h-[70px] w-[120px] sm:h-[90px] sm:w-[140px] md:h-[100px] md:w-[180px] xl:h-[120px] xl:w-[200px] 2xl:h-[140px] 2xl:w-[240px]";
                    }
                  };

                  return (
                    <div
                      key={`participant_${participantId}`}
                      className={`flex ${
                        isPresenting ? getParticipantSize() : "w-full"
                      } items-center justify-center ${
                        isFlexCol && layout === "speaker-side" && isMobile
                          ? "flex-1 min-h-0"
                          : "flex-shrink-0"
                      } ${
                        layout === "speaker-side" && totalParticipants <= 3 && (!isFlexCol || !isMobile)
                          ? "h-[200px]"
                          : ""
                      } ${
                        totalParticipants <= 2 && !isPresenting
                          ? "md:max-w-[400px] 2xl:max-w-[400px] "
                          : !isPresenting
                          ? "md:max-w-lg 2xl:max-w-2xl"
                          : ""
                      } overflow-hidden ${
                        isPresenting ? "rounded-none" : "rounded-lg"
                      } shadow-lg transition-all duration-200 ${
                        isPresenting
                          ? "p-0 hover:shadow-xl hover:scale-105"
                          : "p-1"
                      }`}
                      style={
                        isFlexCol && layout === "speaker-side" && isMobile
                          ? {
                              minWidth: 100,
                              minHeight: 60,
                              maxHeight: "100%",
                            }
                          : {
                              aspectRatio: "16/9",
                              minWidth: 100,
                              minHeight: 130,
                            }
                      }
                    >
                      <MemoizedParticipant
                        participantId={participantId}
                        gridSize={Math.min(participantsPerPage, 7)}
                        totalOnPage={participantsPerPage}
                      />
                    </div>
                  );
                })}
            </>
          )}

          {/* Right Arrow Icon */}
          {isPresenting && totalParticipants > participantsPerPage && (
            <>
              {!isFlexCol && (
                <svg
                  onClick={() => {
                    if (
                      currentPage + participantsPerPage >=
                      totalParticipants
                    ) {
                      return;
                    }
                    setCurrentPage(currentPage + 1);
                  }}
                  disabled={
                    currentPage + participantsPerPage >= totalParticipants
                  }
                  style={{
                    zIndex: 1000,
                    color: "#ffffff",
                    backgroundColor:
                      currentPage + participantsPerPage >= totalParticipants
                        ? "gray"
                        : "#1a1c23",
                    strokeWidth: 4,
                    cursor: "pointer",
                  }}
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  className="w-6 h-full"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M8.25 4.5 15.75 12l-7.5 7.5"
                  />
                </svg>
              )}
              {isFlexCol && (
                <div className="flex justify-center items-center py-2">
                  <button
                    onClick={() => {
                      if (
                        currentPage + participantsPerPage >=
                        totalParticipants
                      ) {
                        return;
                      }
                      setCurrentPage(currentPage + 1);
                    }}
                    disabled={
                      currentPage + participantsPerPage >= totalParticipants
                    }
                    className={`flex items-center justify-center w-8 h-8 rounded-md transition-all duration-200 ${
                      currentPage + participantsPerPage >= totalParticipants
                        ? "bg-gray-700 cursor-not-allowed opacity-40"
                        : "bg-gray-800 hover:bg-gray-700 cursor-pointer"
                    }`}
                    style={{
                      zIndex: 1000,
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={2.5}
                      stroke="currentColor"
                      className="w-4 h-4 text-white"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M19.5 8.25l-7.5 7.5-7.5-7.5"
                      />
                    </svg>
                  </button>
                </div>
              )}
            </>
          )}

          {!isPresenting &&
            (() => {
              // Calculate paginated participants for grid view
              const startIndex = gridCurrentPage * gridParticipantsPerPage;
              const endIndex = startIndex + gridParticipantsPerPage;
              const paginatedParticipants = allParticipantIds.slice(
                startIndex,
                endIndex
              );

              // Calculate optimal grid dimensions based on participant count
              const calculateOptimalGrid = (count) => {
                if (count <= 0) return { cols: 1, rows: 1 };
                if (count === 1) return { cols: 1, rows: 1 };
                if (count === 2) return { cols: 2, rows: 1 };
                if (count === 3) return { cols: 2, rows: 2 };
                if (count === 4) return { cols: 2, rows: 2 };
                if (count <= 6) return { cols: 3, rows: 2 };
                if (count <= 9) return { cols: 3, rows: 3 };
                if (count <= 12) return { cols: 4, rows: 3 };
                if (count <= 16) return { cols: 4, rows: 4 };
                if (count <= 20) return { cols: 5, rows: 4 };
                if (count <= 25) return { cols: 5, rows: 5 };
                if (count <= 30) return { cols: 6, rows: 5 };
                if (count <= 36) return { cols: 6, rows: 6 };
                if (count <= 42) return { cols: 7, rows: 6 };
                // Max per page is 25 (5x5), but function can handle up to 7x7 = 49 participants
                return { cols: 7, rows: 7 };
              };

              // Calculate perRow based on current page participants, not total participants
              const currentPageParticipants = paginatedParticipants.length;
              let perRow;
              let gridRows;

              const useDesktopGrid = !isMobile && !isPresenting;

              if (!useDesktopGrid) {
                perRow =
                  currentPageParticipants < 4
                    ? 1
                    : currentPageParticipants < 9
                    ? 2
                    : 3;
                gridRows = Math.ceil(currentPageParticipants / perRow);
              } else {
                // Use dynamic grid calculation for desktop
                const grid = calculateOptimalGrid(currentPageParticipants);
                perRow = grid.cols;
                gridRows = grid.rows;
              }

              return (
                <>
                  {/* Render all participants for audio, but hide non-current page ones */}
                  {allParticipantIds.map((participantId, idx) => {
                    const isOnCurrentPage = idx >= startIndex && idx < endIndex;
                    if (!isOnCurrentPage) {
                      return (
                        <div
                          key={`participant_hidden_${participantId}`}
                          style={{ display: "none" }}
                        >
                          <MemoizedParticipant
                            participantId={participantId}
                            gridSize={perRow}
                            totalOnPage={currentPageParticipants}
                          />
                        </div>
                      );
                    }
                    return null;
                  })}

                  {/* Display visible participants with layout */}
                  {(() => {
                    if (
                      totalParticipants === 1 ||
                      currentPageParticipants === 1
                    ) {
                      return (
                        <div className="flex flex-1 items-center justify-center h-full w-full">
                          {paginatedParticipants.map((participantId, index) => {
                            return (
                              <div
                                key={`participant_${participantId}`}
                                className="flex items-center justify-center overflow-hidden w-full h-full"
                              >
                                <MemoizedParticipant
                                  participantId={participantId}
                                  isSingleParticipant={true}
                                  gridSize={1}
                                  totalOnPage={1}
                                />
                              </div>
                            );
                          })}
                        </div>
                      );
                    }

                    // OLD STYLE: Equal‑sized grid tiles (default)
                    if (!ENABLE_DYNAMIC_LAYOUT) {
                      return (
                        <div className="flex h-full w-full bg-gray-700">
                          <div
                            className="grid h-full w-full gap-[2px] bg-gray-700"
                            style={{
                              gridTemplateColumns: `repeat(${perRow}, minmax(0, 1fr))`,
                            }}
                          >
                            {paginatedParticipants.map((participantId) => (
                              <div
                                key={`participant_${participantId}`}
                                className="flex items-center justify-center overflow-hidden w-full h-full"
                                style={{
                                  aspectRatio: "16/9",
                                  minWidth: 0,
                                  minHeight: 0,
                                }}
                              >
                                <MemoizedParticipant
                                  participantId={participantId}
                                  gridSize={perRow}
                                  totalOnPage={currentPageParticipants}
                                />
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    }

                    // Dynamic layout
                    return (
                      <div
                        className="relative h-full w-full min-h-0 max-h-full overflow-hidden"
                        style={{ width: "100%", height: "100%" }}
                      >
                        <div
                          ref={gridViewportRef}
                          className="absolute inset-0"
                          style={{ width: "100%", height: "100%" }}
                        >
                          {(() => {
                            const rect = gridViewportEl
                              ? gridViewportEl.getBoundingClientRect()
                              : null;
                            const cw =
                              gridViewport.width > 10
                                ? gridViewport.width
                                : rect?.width || 1200;
                            const ch =
                              gridViewport.height > 10
                                ? gridViewport.height
                                : rect?.height || 700;

                            const layoutInput = paginatedParticipants.map(
                              (id) => {
                                return { id, aspectRatio: 16 / 9 };
                              }
                            );

                            const horizontalPadding = 12;

                            const {
                              items: optimalLayoutItems,
                              optimalGridWidth,
                              sidePadding,
                              contentHeight: optimalContentHeight,
                            } = computeOptimalGridLayout({
                              participants: layoutInput,
                              containerWidth: cw,
                              containerHeight: ch,
                              gap: 2,
                              padding: horizontalPadding,
                              minRowHeight: 110,
                              maxRows: useDesktopGrid ? gridRows : 7,
                              ...(useDesktopGrid
                                ? { maxItemsPerRow: perRow }
                                : {}),
                              targetAspectRatio: 16 / 9,
                            });

                            return (
                              <div
                                style={{
                                  position: "absolute",
                                  left: `${sidePadding}px`,
                                  top: "0px",
                                  width: `${optimalGridWidth}px`,
                                  height: "100%",
                                  maxHeight: "100%",
                                  overflow: "hidden",
                                }}
                              >
                                <div
                                  style={{
                                    position: "relative",
                                    width: "100%",
                                    height: "100%",
                                    overflow: "hidden",
                                  }}
                                >
                                  {optimalLayoutItems.map((it) => {
                                    const p =
                                      participants?.get && it.id
                                        ? participants.get(it.id)
                                        : null;
                                    const meta = p?.metaData || {};
                                    const deviceType =
                                      meta.deviceType ||
                                      (meta.isMobile ? "mobile" : "desktop");
                                    const isMobileParticipant =
                                      deviceType === "mobile";

                                    const webcamOn = p?.webcamOn ?? false;

                                    return (
                                      <div
                                        key={`participant_${it.id}`}
                                        className={`absolute overflow-hidden ${
                                          !webcamOn ? "bg-gray-750" : ""
                                        }`}
                                        style={{
                                          left: `${it.x}px`,
                                          top: `${it.y}px`,
                                          width: `${it.width}px`,
                                          height: `${it.height}px`,
                                          minWidth: 0,
                                          minHeight: 0,

                                          backgroundColor: webcamOn
                                            ? "transparent"
                                            : undefined,
                                          transition:
                                            "left 220ms ease, top 220ms ease, width 220ms ease, height 220ms ease",
                                          willChange:
                                            "left, top, width, height",
                                        }}
                                      >
                                        <MemoizedParticipant
                                          participantId={it.id}
                                          gridSize={it.rowSize}
                                          totalOnPage={currentPageParticipants}
                                          isMobileParticipant={
                                            isMobileParticipant
                                          }
                                        />
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    );
                  })()}
                </>
              );
            })()}
        </div>

        {!isPresenting &&
          !isMobile &&
          totalParticipants > gridParticipantsPerPage && (
            <div
              className={`fixed flex items-center gap-3 z-[9999] bg-gray-900/90 backdrop-blur-sm rounded-full px-5 py-3 shadow-2xl border border-gray-700 transition-all duration-200 ${
                isDragging
                  ? "cursor-grabbing scale-105 shadow-3xl"
                  : "cursor-grab"
              }`}
              style={{
                left: `${panelPosition.x}px`,
                top: `${panelPosition.y}px`,
              }}
              onMouseDown={handleMouseDown}
            >
              {/* Previous Button */}
              <button
                onClick={() => {
                  if (gridCurrentPage > 0) {
                    setGridCurrentPage(gridCurrentPage - 1);
                  }
                }}
                disabled={gridCurrentPage === 0}
                className={`flex items-center justify-center w-7 h-7 rounded-full transition-all duration-200 ${
                  gridCurrentPage === 0
                    ? "bg-gray-700 cursor-not-allowed opacity-40"
                    : "bg-[#7700ff] hover:bg-[#9933ff] cursor-pointer"
                }`}
                style={{ color: "#ffffff" }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={2.5}
                  stroke="currentColor"
                  className="w-4 h-4"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15.75 19.5 8.25 12l7.5-7.5"
                  />
                </svg>
              </button>

              {/* Page Counter */}
              <div className="flex items-center gap-1 text-white text-sm font-medium px-2">
                <span>{gridCurrentPage + 1}</span>
                <span className="text-gray-400">/</span>
                <span>
                  {Math.ceil(totalParticipants / gridParticipantsPerPage)}
                </span>
              </div>

              {/* Next Button */}
              <button
                onClick={() => {
                  const totalPages = Math.ceil(
                    totalParticipants / gridParticipantsPerPage
                  );
                  if (gridCurrentPage < totalPages - 1) {
                    setGridCurrentPage(gridCurrentPage + 1);
                  }
                }}
                disabled={
                  gridCurrentPage >=
                  Math.ceil(totalParticipants / gridParticipantsPerPage) - 1
                }
                className={`flex items-center justify-center w-7 h-7 rounded-full transition-all duration-200 ${
                  gridCurrentPage >=
                  Math.ceil(totalParticipants / gridParticipantsPerPage) - 1
                    ? "bg-gray-700 cursor-not-allowed opacity-40"
                    : "bg-[#7700ff] hover:bg-[#9933ff] cursor-pointer"
                }`}
                style={{ color: "#ffffff" }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={2.5}
                  stroke="currentColor"
                  className="w-4 h-4"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M8.25 4.5 15.75 12l-7.5 7.5"
                  />
                </svg>
              </button>
            </div>
          )}
      </div>
    </>
  );
}

export const MemoizedParticipantGrid = React.memo(
  ParticipantGrid,
  (prevProps, nextProps) => {
    return (
      JSON.stringify(prevProps.participantIds) ===
      JSON.stringify(nextProps.participantIds) &&
      prevProps.isPresenting === nextProps.isPresenting
    );
  }
);
